import { useMemo } from 'react';
import { useContent } from '../contexts/ContentContext';
import { useSiteConfig } from '../contexts/SiteConfigContext';

const DEFAULT_KEYS = {
  eyebrow: 'HomeOperatedBy',
  banner: 'HomeBannerText',
  subtitle: 'HomeParticipatingText',
  cta: 'HomeStandardCTAButton',
};

const hasText = (value) => String(value || '').trim().length > 0;

const cleanText = (value) => String(value || '').replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();

const getFirstText = (...values) => values.find(hasText) || '';

const readConfigValue = (config, ...keys) => {
  for (const key of keys) {
    if (hasText(config?.[key])) return config[key];
  }
  return '';
};

const getSectionConfig = (sections, sectionType) => (
  sections?.find(section => section.section_type === sectionType)?.config || {}
);

const shouldUseCharityOperatorName = (siteDetails) => {
  const sd = siteDetails || {};
  return (
    sd.operatedByCharity === true ||
    sd.isOperatedByCharity === true ||
    sd.operated_by_charity === true
  );
};

const shouldAppendOperatorName = (label, operatorName) => {
  if (!hasText(operatorName)) return false;

  const cleanedLabel = cleanText(label).replace(/[:.\s]+$/g, '').toLowerCase();
  const cleanedOperator = cleanText(operatorName).toLowerCase();
  if (!cleanedLabel || cleanedLabel.includes(cleanedOperator)) return false;

  return cleanedLabel === 'operated by' ||
    cleanedLabel === 'powered by' ||
    cleanedLabel === 'supporting' ||
    cleanedLabel.length <= 20;
};

export function useHeroContent({
  mainDraw,
  partner,
  gameType = 'classic5050',
  sectionConfig,
  sections,
  fallbackTitle,
  fallbackSubtitle,
  fallbackCta = 'Buy Tickets',
} = {}) {
  const { c } = useContent();
  const { siteDetails } = useSiteConfig();

  return useMemo(() => {
    const heroSectionConfig = getSectionConfig(sections, 'hero_section');
    const heroCarouselConfig = getSectionConfig(sections, 'hero_carousel');
    // Merge so a hero_section row takes precedence, but a hero_carousel row
    // (used by classic5050/modified5050/etc. defaults) still drives the editor
    // fields when no hero_section config is present.
    const config = sectionConfig
      || { ...heroCarouselConfig, ...heroSectionConfig };

    const eyebrowKey = readConfigValue(config, 'eyebrowContentKey', 'operatedByContentKey') || DEFAULT_KEYS.eyebrow;
    const bannerKey = readConfigValue(config, 'bannerContentKey', 'titleContentKey') || DEFAULT_KEYS.banner;
    const subtitleKey = readConfigValue(config, 'subtitleContentKey', 'participatingContentKey') || DEFAULT_KEYS.subtitle;
    const ctaKey = readConfigValue(config, 'ctaContentKey', 'buttonContentKey') || DEFAULT_KEYS.cta;

    const siteName = getFirstText(
      config?.organizationName,
      siteDetails?.name,
      siteDetails?.siteName,
      siteDetails?.organizationName,
      partner?.partner_name,
      mainDraw?.partner_name,
      mainDraw?.charity_name,
      mainDraw?.title,
      gameType === 'modified5050' ? 'Modified 50/50' : 'Classic 50/50'
    );

    const operatorName = getFirstText(
      config?.operatorName,
      shouldUseCharityOperatorName(siteDetails) ? siteDetails?.name : '',
      siteDetails?.operatingName,
      siteDetails?.operatorName,
      siteDetails?.organizationName,
      siteName
    );

    const drawTitleFallback = getFirstText(
      fallbackTitle,
      mainDraw?.name,
      mainDraw?.title,
      gameType === 'modified5050' ? 'Modified 50/50' : '50/50'
    );

    const subtitleFallback = getFirstText(
      fallbackSubtitle,
      c('HomeParticipatingCaption', ''),
      "By participating in this fundraiser you're directly supporting our community programs and services."
    );

    const eyebrowLabel = readConfigValue(config, 'eyebrowText', 'operatedByText') ||
      c(eyebrowKey, 'Operated by');
    const bannerText = readConfigValue(config, 'bannerText', 'titleText') ||
      c(bannerKey, drawTitleFallback);
    const subtitleText = readConfigValue(config, 'subtitleText', 'participatingText') ||
      c(subtitleKey, subtitleFallback);
    const ctaText = readConfigValue(config, 'ctaText', 'buttonText') ||
      c(ctaKey, fallbackCta);

    return {
      eyebrowKey,
      eyebrowLabel,
      operatorName,
      showOperatorName: shouldAppendOperatorName(eyebrowLabel, operatorName),
      organizationName: siteName,
      bannerKey,
      bannerText,
      bannerFallback: drawTitleFallback,
      subtitleKey,
      subtitleText,
      subtitleFallback,
      ctaKey,
      ctaText,
      ctaFallback: fallbackCta,
    };
  }, [
    c,
    fallbackCta,
    fallbackSubtitle,
    fallbackTitle,
    gameType,
    mainDraw,
    partner,
    sectionConfig,
    sections,
    siteDetails,
  ]);
}

export default useHeroContent;
